import React from "react";
import PropTypes from "prop-types";
import "./StyleSheet.css";

/**
 * A helper function to format database-style keys (e.g., "document_number")
 * into a more readable, title-cased format (e.g., "Document Number").
 */
const formatHeader = (key) => {
	if (!key) return "";
	return key
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

//  Helper to detect if a column contains long text (descriptions, summaries, titles)
// so it can wrap properly while keeping IDs/codes on one line.
const isLongTextColumn = (headerName, value) => {
	const lower = (headerName || "").toLowerCase();
	if (
		lower.includes("desc") ||
		lower.includes("summary") ||
		lower.includes("comment") ||
		lower.includes("detail") ||
		lower.includes("text") ||
		lower.includes("title")
	) {
		return true;
	}
	return typeof value === "string" && value.length > 35;
};

/**
 * A presentational component designed specifically to render a bot response that
 * contains tabular data from the SQL agent. It displays an introductory text
 * followed by a fully styled and responsive HTML table.
 */
const SqlTableMessage = ({ message }) => {
	const { introText, tableData, sqlFileUrls } = message;

	// Fallback if data is invalid or empty
	if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
		return (
			<p className="sql-intro-text">{introText || "No data to display."}</p>
		);
	}

	const allHeaders = Object.keys(tableData[0]);
	const headersToDisplay = allHeaders.filter((header) => header !== "file_url");

	//Dynamically calculate min-width based on column count so headers/text never render vertically
	const calculatedMinWidth = Math.max(600, headersToDisplay.length * 130);

	return (
		<div className="sql-table-container">
			{/* Render the introText variable inside the p tag */}
			{introText && <p className="sql-intro-text">{introText}</p>}

			<div className="table-wrapper">
				{/* Set dynamic minWidth to prevent columns from collapsing in narrow add-in pane */}
				<table
					className="sql-response-table"
					style={{ minWidth: `${calculatedMinWidth}px` }}
				>
					<thead>
						<tr>
							{headersToDisplay.map((header) => (
								<th key={header}>{formatHeader(header)}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{tableData.map((row, rowIndex) => {
							const fileUrl = sqlFileUrls ? sqlFileUrls[rowIndex] : null;
							return (
								<tr key={rowIndex}>
									{headersToDisplay.map((header) => {
										const cellValue = row[header];
										// Check if cell has long text
										const isLong = isLongTextColumn(header, cellValue);

										return (
											<td
												key={`${rowIndex}-${header}`}
												// Apply text-content-cell class to wrap long text and keep others nowrap
												className={isLong ? "text-content-cell" : ""}
												data-label={formatHeader(header)}
											>
												{/* Conditionally render the document number as a link */}
												{header === "document_number" && fileUrl ? (
													<a
														href={fileUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="table-link"
													>
														{cellValue}
													</a>
												) : typeof cellValue === "object" &&
													cellValue !== null ? (
													JSON.stringify(cellValue)
												) : (
													String(cellValue ?? "")
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

SqlTableMessage.propTypes = {
	message: PropTypes.shape({
		introText: PropTypes.string,
		tableData: PropTypes.arrayOf(PropTypes.object).isRequired,
		sqlFileUrls: PropTypes.arrayOf(PropTypes.string),
	}).isRequired,
};
export default SqlTableMessage;